import axios from 'axios';

import type { CustomField } from 'bundles/apex/custom_fields/types';
import type { IBulkRowHook, IDeveloperField, IReviewStepData, IRowHookInput, IStepHook } from 'dromo-uploader-js';

type StepHookParams = Parameters<IStepHook['callback']>;
type Dromo = StepHookParams[0];
type StepHookData = StepHookParams[1];

type Query = string | number;
type IdsByQuery = Record<Query, number>;

interface Keys {
  idKey: string;
  queryKey: string;
}

interface HookOptions {
  force?: boolean;
}

export function buildContactIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, `/crm/imports/find_contact_ids`, options);
}

export function buildPropertyIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, `/crm/imports/find_property_ids`, options);
}

export function buildUserIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, `/crm/imports/find_user_ids`, options);
}

interface AssetTypeKeys {
  assetTypeKey: string;
  assetQueryKey: string;
  assetIdKey: string;
}

const ASSET_TYPE_PATHS: Record<string, string> = {
  'contact': '/crm/imports/find_contact_ids',
  'property': '/crm/imports/find_property_ids',
  'deal': '/crm/imports/find_deal_ids',
};

export function buildDynamicAssetIdLookupHooks(label: string, assetKeys: AssetTypeKeys, options?: HookOptions): {
  stepHook: IStepHook;
  bulkRowHook: IBulkRowHook;
} {
  return {
    stepHook: {
      type: 'REVIEW_STEP',
      callback: (instance, data) => {
        const keys = { idKey: assetKeys.assetIdKey, queryKey: assetKeys.assetQueryKey };
        addIdField(instance, data, label, keys, options || {});
      },
    },
    bulkRowHook: (records: IRowHookInput[]) => {
      return loadDynamicAssetIds(records, assetKeys, options);
    },
  };
}

async function loadDynamicAssetIds(records: IRowHookInput[], assetKeys: AssetTypeKeys, options: HookOptions = {}) {
  if (!records.length || !Object.prototype.hasOwnProperty.call(records[0].row, assetKeys.assetIdKey)) {
    return records;
  }

  // Clear ID field and initialize info arrays when there's a query value
  // This ensures we do a fresh lookup when asset type or asset name changes
  for (const record of records) {
    const idCell = record.row[assetKeys.assetIdKey];
    const queryCell = record.row[assetKeys.assetQueryKey];

    if (idCell) {
      idCell.info = [];

      // Clear the ID if there's a query value, forcing a fresh lookup
      if (queryCell?.value) {
        idCell.value = null;
        idCell.resultValue = null;
      }
    }
  }

  // Group records by asset type
  const recordsByType = new Map<string, IRowHookInput[]>();
  for (const record of records) {
    const assetType = record.row[assetKeys.assetTypeKey]?.value;
    if (assetType && typeof assetType === 'string') {
      if (!recordsByType.has(assetType)) {
        recordsByType.set(assetType, []);
      }
      recordsByType.get(assetType)!.push(record);
    }
  }

  const lookupPromises = Array.from(recordsByType.entries()).map(async ([assetType, typeRecords]) => {
    const path = ASSET_TYPE_PATHS[assetType.toLowerCase()];
    if (!path) {
      // Mark records with unknown asset type as errors
      for (const record of typeRecords) {
        const idCell = record.row[assetKeys.assetIdKey];
        if (idCell) {
          idCell.info = [{
            level: 'error',
            message: `Unknown asset type: ${assetType}`,
          }];
        }
      }
      return;
    }

    // Get queries for this type
    const keys = { idKey: assetKeys.assetIdKey, queryKey: assetKeys.assetQueryKey };
    const queries = getQueries(typeRecords, keys);

    if (queries.length) {
      try {
        const response = await axios.post(path, { queries });
        const idsByQuery = response.data as IdsByQuery;
        for (const record of typeRecords) {
          setId(record, idsByQuery, keys, options);
        }
      } catch {
        // Mark all records of this type with an error
        for (const record of typeRecords) {
          const idCell = record.row[assetKeys.assetIdKey];
          if (idCell) {
            idCell.info = [{
              level: 'error',
              message: `Failed to lookup ${assetType}`,
            }];
          }
        }
      }
    }
  });

  await Promise.all(lookupPromises);

  return records;
}

function buildIdLookupHooks(label: string, keys: Keys, path: string, options: HookOptions = {}): {
  stepHook: IStepHook;
  bulkRowHook: IBulkRowHook;
} {
  return {
    stepHook: {
      type: 'REVIEW_STEP',
      callback: (instance, data) => {
        addIdField(instance, data, label, keys, options);
      },
    },
    bulkRowHook: (records: IRowHookInput[]) => {
      return loadIds(records, keys, path, options);
    },
  };
}

function addIdField(instance: Dromo, looselyTypedData: StepHookData, label: string, keys: Keys, options: HookOptions) {
  const data = looselyTypedData as IReviewStepData;
  if (!data.fields[keys.idKey] && (data.fields[keys.queryKey] || options.force)) {
    const field: IDeveloperField = {
      key: keys.idKey,
      label,
      type: ['number', 'integer'],
    };

    if (options.force) {
      field.validators = [{ validate: 'required' }];
    }

    // TODO: Could make this a dropdown, so the user can:
    //       * Choose an ID (might be multiple matches)
    //       * Choose none to create a new record
    instance.addField(field, { before: keys.queryKey });
  }
}

async function loadIds(records: IRowHookInput[], keys: Keys, path: string, options: HookOptions) {
  if (!records.length || !Object.prototype.hasOwnProperty.call(records[0].row, keys.idKey)) {
    return records;
  }

  for (const record of records) {
    record.row[keys.idKey].info = [];
  }

  const queries = getQueries(records, keys);

  if (queries.length) {
    const response = await axios.post(path, { queries });
    const idsByQuery = response.data as IdsByQuery;
    for (const record of records) {
      setId(record, idsByQuery, keys, options);
    }
  }

  return records;
}

function getQueries(records: IRowHookInput[], keys: Keys): Query[] {
  const queries = new Set<string | number>();
  for (const record of records) {
    const query = record.row[keys.queryKey]?.value;
    const existingId = record.row[keys.idKey]?.resultValue;
    if (existingId) {
      queries.add(existingId);
    } else if (query) {
      queries.add(query);
    }
  }

  return [...queries];
}

function setId(record: IRowHookInput, idsByQuery: IdsByQuery, keys: Keys, options: HookOptions) {
  const idCell = record.row[keys.idKey];
  const existingId = idCell?.resultValue || null;

  let foundId: number;
  if (existingId) {
    foundId = idsByQuery[existingId];
  } else {
    const queryCell = record.row[keys.queryKey];
    if (!queryCell) {
      return;
    }

    foundId = idsByQuery[queryCell?.value];
  }

  idCell.value = idCell.resultValue = foundId || existingId;

  if (!foundId) {
    idCell.info = [{
      level: options.force ? 'error' : 'warning',
      message: 'Not found',
    }];
  }
}

export function mapCustomFieldsToDromoFields(customFields: CustomField[]): IDeveloperField[] {
  return customFields.map((customField) => {
    let options, type;
    if (customField.type === 'currency') {
      type = ['number', 'usd'];
    } else if (customField.type === 'date') {
      type = ['date', { displayFormat: 'MM/dd/yyyy' }];
    } else if (customField.type === 'float' || customField.type === 'number') {
      type = 'number';
    } else if (customField.type === 'percentage') {
      type = ['number', 'percent'];
    } else if (customField.options?.length) {
      type = 'select';
      options = customField.options.map(option => ({ label: option, value: option }));
    } else {
      type = 'string';
    }

    return {
      key: customField.name,
      label: customField.label,
      selectOptions: options,
      type,
    };
  });
}

export function addDefaultValidators(fields: IDeveloperField[]) {
  for (const field of fields) {
    field.validators = field.validators || [];

    if (field.type === 'string' && !field.validators.find(validator => validator.validate === 'length')) {
      field.validators.push({ validate: 'length', max: 255 });
    }
  }

  return fields;
}
